import { useLocale } from 'containers/App/LocaleContext';
import { type FC, useMemo, useState } from 'react';
import type { AppTranslations } from 'translations/translations';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { NoSnapshots } from './NoSnapshots';
import { SnapshotFilters } from './SnapshotFilters';
import { SnapshotSection } from './SnapshotSection';
import type { SnapshotList } from './SnapshotTypes';
import { useMultiSelection } from './useMultiSelection';
import { useSectionState } from './useSectionState';
import {
  concatSnapshots,
  filterOnlyLoadedSnapshots,
  filterSnapshotsByName,
  sortSnapshotsByDate,
  sortSnapshotsByGroup,
} from './utils';

type Props = {
  snapshotsList: SnapshotList;
  selectedSnapshot?: string[] | null;
  msg: AppTranslations['snapshotManagement'];
  onAddSelectedItems: (snapshotIds: string[]) => void;
};

export const SnapshotsList: FC<Props> = (props) => {
  const [searchValue, setSearchValue] = useState('');
  const { snapshotWithoutName } = useLocale().generic.components.snapshotsList;
  const [showUnloadedSnapshots, setShowUnloadedSnapshots] = useState(true);
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();

  // Combine all snapshot transformations into a single memoized function
  const { processedSnapshots, numberOfUnloadedSnapshots } = useMemo(() => {
    const allSnapshots = concatSnapshots(props.snapshotsList);
    const nameFiltered = filterSnapshotsByName(
      allSnapshots,
      searchValue,
      snapshotWithoutName,
    );
    const onlyLoadedSnapshots = filterOnlyLoadedSnapshots(nameFiltered);
    const numberOfUnloadedSnapshots =
      nameFiltered.length - onlyLoadedSnapshots.length;

    const stateFiltered = showUnloadedSnapshots
      ? nameFiltered
      : onlyLoadedSnapshots;
    return {
      processedSnapshots: sortSnapshotsByDate(stateFiltered),
      numberOfUnloadedSnapshots: numberOfUnloadedSnapshots,
    };
  }, [
    props.snapshotsList,
    showUnloadedSnapshots,
    searchValue,
    snapshotWithoutName,
  ]);

  // Sort and group snapshots into sections (today, last week, xxx month, etc.)
  const sections = useMemo(
    () => sortSnapshotsByGroup(processedSnapshots, props.selectedSnapshot),

    [processedSnapshots, props.selectedSnapshot],
  );

  const { handleSelectSnapshot } = useMultiSelection({
    snapshots: processedSnapshots,
    selectedSnapshotIds: props.selectedSnapshot,
    onAddSelectedItems: props.onAddSelectedItems,
  });

  // Custom hook for section state management
  const { openedSections, handleSectionToggle, handleSearchStateChange } =
    useSectionState({
      sections,
      selectedSnapshot: props.selectedSnapshot,
    });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    handleSearchStateChange(value);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    handleSearchStateChange('');
  };

  return (
    <div className='mx-4 flex flex-col h-full gap-2'>
      <div className='text-level-5 font-heading font-semibold mt-2'>
        List of snapshots
      </div>
      <SnapshotFilters
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        showUnloadedSnapshots={showUnloadedSnapshots}
        onShowUnloadedChange={setShowUnloadedSnapshots}
        unloadedCount={numberOfUnloadedSnapshots}
      />

      <div>
        {!sections.length && (
          <NoSnapshots
            variant={searchValue === '' ? 'no-snapshots-yet' : 'no-results'}
          />
        )}
        {sections.map((section) => {
          const sectionIsOpened = openedSections[section.title] ?? false;
          return (
            <SnapshotSection
              key={section.title}
              section={section}
              isOpen={sectionIsOpened}
              selectedSnapshot={props.selectedSnapshot}
              onSectionToggle={() => handleSectionToggle(section.title)}
              onSelectSnapshot={handleSelectSnapshot}
              msg={props.msg}
              defaultSnapshotName={snapshotWithoutName}
              dateTimeFormat={dateTimeFormat}
              timezone={timezone}
            />
          );
        })}
      </div>
    </div>
  );
};
